import React from 'react';
import { Box } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch } from 'store/store';
import { createUser } from 'store/actions/userActions';
import { UserFormFields, type UserFormValues } from 'components/Users/UserFormFields';
import RedButton from 'Styled/RedButton';
import TextButton from 'Styled/TextButton';
import { isFulfilled } from '@reduxjs/toolkit';
import { assignablePermissions } from 'types/enums/Permission';
import { PASSWORD_HINT, passwordSchema } from 'utility/passwordRules';
import { PageHeader } from 'components/Layout/PageHeader';
import { preventEnterSubmit } from 'utility/preventEnterSubmit';

const validationSchema = Yup.object({
  username: Yup.string().min(5, 'Mindestens 5 Zeichen').required('Benutzername ist erforderlich'),
  email: Yup.string().email('Ungültige E-Mail').required('E-Mail ist erforderlich'),
  password: passwordSchema.required('Passwort ist erforderlich'),
  passwordRepeat: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwörter stimmen nicht überein')
    .required('Bitte das Passwort wiederholen'),
  permissionId: Yup.number().required('Rolle ist erforderlich'),
});

const initialValues: UserFormValues = { username: '', email: '', password: '', passwordRepeat: '', permissionId: '' };

export const UserCreatePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { lotteryId } = useParams<{ lotteryId: string }>();
  const permissionOptions = assignablePermissions();

  const backToList = () => navigate(`/lottery/${lotteryId}/users`);

  return (
    <Box>
      <PageHeader title="Neuer Benutzer" />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          const result = await dispatch(
            createUser({
              lotteryId: Number(lotteryId),
              username: values.username,
              email: values.email,
              password: values.password,
              permissionId: Number(values.permissionId),
            } as { lotteryId: number; username: string; email: string; password: string; permissionId: number })
          );
          if (isFulfilled(result)) {
            backToList();
          }
        }}
      >
        {(form) => (
          <Form onKeyDown={preventEnterSubmit}>
            <Box sx={{ maxWidth: 600, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <UserFormFields form={form} options={permissionOptions} passwordHint={PASSWORD_HINT} />

              <Box sx={{ display: 'flex', gap: 2, marginTop: 2 }}>
                <RedButton type="submit" disabled={form.isSubmitting}>
                  Erstellen
                </RedButton>
                <TextButton onClick={backToList}>Abbrechen</TextButton>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
